import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CalendarDays, Sparkles } from "lucide-react";
import api from "../../lib/api";
import { useApp } from "../../context/AppContext";
import SEO from "../../components/SEO";
import { PageHero, EmptyState, LazyImg, ShareButtons } from "../../components/Bits";
import { pickLocalized } from "../../lib/localized";

const senderName = (g) => g?.sender?.sender_name || g?.sender?.name || "Sahaj Sarathi Foundation";
const senderOrg = (g) => g?.sender?.organisation || "Sahaj Sarathi Foundation";
const englishText = (g) => pickLocalized(g?.message, "en");
const hindiText = (g) => pickLocalized(g?.message_hi, "hi") || pickLocalized(g?.message, "hi");
const greetingTitle = (g) => pickLocalized(g?.title);
const greetingQuote = (g) => pickLocalized(g?.quote);

function BilingualMessage({ g, testid }) {
  const en = englishText(g);
  const hi = hindiText(g);
  if (!en || !hi || en === hi) return <p className="mt-4 whitespace-pre-wrap text-sm leading-relaxed text-slate-700 sm:text-base" data-testid={testid}>{hi || en}</p>;
  return (
    <div className="mt-4 space-y-4" data-testid={testid}>
      <div>
        <p className="text-[11px] font-bold uppercase tracking-wider text-saffron-600">English</p>
        <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-slate-700 sm:text-base">{en}</p>
      </div>
      <div>
        <p className="text-[11px] font-bold uppercase tracking-wider text-saffron-600">हिन्दी</p>
        <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-slate-700 sm:text-base">{hi}</p>
      </div>
    </div>
  );
}

function SenderCard({ g }) {
  return (
    <div className="mt-6 rounded-xl border border-gray-200 bg-white p-5" data-testid="greeting-sender">
      <p className="font-display text-lg font-bold text-navy">{senderName(g)}</p>
      {g.sender?.designation && <p className="mt-0.5 text-xs font-semibold text-slate-500">{g.sender.designation}</p>}
      <p className="mt-1 text-xs text-slate-500">{senderOrg(g)}</p>
    </div>
  );
}

export function Greetings() {
  const { t, lang } = useApp();
  const [data, setData] = useState({ items: [] });
  const [today, setToday] = useState(null);
  const [category, setCategory] = useState("");

  useEffect(() => {
    api.get("/public/greetings/today").then((r) => setToday(r.data?.id ? r.data : null)).catch(() => {});
  }, []);
  useEffect(() => {
    api.get(`/public/greetings?limit=48${category ? `&category=${encodeURIComponent(category)}` : ""}`).then((r) => setData(r.data)).catch(() => {});
  }, [category]);

  const categories = [...new Set(data.items.map((g) => g.category).filter(Boolean))];
  const shareText = today ? [...new Set([englishText(today), hindiText(today)].filter(Boolean))].join("\n\n") : "";
  const todayTitle = greetingTitle(today);

  return (
    <div data-testid="greetings-page">
      <SEO title={t("greetings")} description="Approved festival greetings, national day wishes, awareness messages and inspirational greetings from Sahaj Sarathi Foundation." />
      <PageHero title={t("greetings")} sub={lang === "hi" ? "सहज सारथी फाउंडेशन की अनुमोदित शुभकामनाएँ।" : "Approved greetings and awareness messages from Sahaj Sarathi Foundation."} />
      {today && (
        <section className="border-b border-gray-200 bg-cream py-12" data-testid="greeting-today-section">
          <div className="container-ssf grid items-center gap-8 md:grid-cols-[320px_1fr]">
            {today.image && (
              <Link to={`/greetings/${today.slug}`} className="card-ssf block overflow-hidden rounded-2xl transition-transform duration-200 hover:-translate-y-1">
                <LazyImg src={today.image} alt={todayTitle} className="aspect-square w-full object-cover" />
              </Link>
            )}
            <div>
              <p className="label-eyebrow"><Sparkles size={12} className="mr-1 inline" />{t("todays_greeting")}</p>
              <h2 className="mt-2 font-display text-2xl font-bold text-navy sm:text-3xl">{todayTitle}</h2>
              <div className="mt-2 flex flex-wrap gap-2 text-xs font-semibold text-slate-500">
                {today.occasion && <span className="rounded-full bg-white px-3 py-1">{today.occasion}</span>}
                {today.date && <span className="inline-flex items-center gap-1 rounded-full bg-white px-3 py-1"><CalendarDays size={12} />{today.date}</span>}
              </div>
              <BilingualMessage g={today} testid="greeting-today-message" />
              {greetingQuote(today) && (
                <blockquote className="mt-4 border-l-4 border-saffron-500 pl-4 text-sm italic text-slate-600">
                  “{greetingQuote(today)}”{today.quote_author && <span className="not-italic font-semibold"> — {today.quote_author}</span>}
                </blockquote>
              )}
              <SenderCard g={today} />
              <div className="mt-6 flex flex-wrap items-center gap-4">
                <Link to={`/greetings/${today.slug}`} className="btn-primary !px-5 !py-2.5 text-sm" data-testid="greeting-today-view">{t("view_greeting")}</Link>
                <ShareButtons title={todayTitle} text={shareText} url={`${window.location.origin}/greetings/${today.slug}`} />
              </div>
            </div>
          </div>
        </section>
      )}
      <section className="py-16">
        <div className="container-ssf">
          {categories.length > 1 && (
            <div className="mb-8 flex flex-wrap gap-2">
              <button onClick={() => setCategory("")} data-testid="greetings-filter-all"
                className={`rounded-full px-5 py-2 text-sm font-semibold transition-colors ${!category ? "bg-navy text-white" : "bg-white text-navy border border-gray-200"}`}>{lang === "hi" ? "सभी" : "All"}</button>
              {categories.map((c) => (
                <button key={c} onClick={() => setCategory(c)} data-testid={`greetings-filter-${c}`}
                  className={`rounded-full px-5 py-2 text-sm font-semibold transition-colors ${category === c ? "bg-navy text-white" : "bg-white text-navy border border-gray-200"}`}>{c}</button>
              ))}
            </div>
          )}
          {data.items.length === 0 && <EmptyState text="Approved greetings will appear here once published." />}
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {data.items.map((g) => (
              <Link key={g.id} to={`/greetings/${g.slug}`} data-testid={`greeting-card-${g.slug}`}
                className="card-ssf group overflow-hidden transition-[transform,box-shadow] duration-200 hover:-translate-y-1 hover:shadow-xl">
                {g.image && <LazyImg src={g.image} alt={greetingTitle(g)} className="aspect-square w-full object-cover" />}
                <div className="p-5">
                  <span className="text-xs font-bold uppercase tracking-wider text-saffron-600">{g.occasion || g.category}</span>
                  <h2 className="mt-1 font-display text-lg font-bold text-navy group-hover:text-saffron-600">{greetingTitle(g)}</h2>
                  {g.date && <p className="mt-1.5 flex items-center gap-1.5 text-xs text-slate-500"><CalendarDays size={13} />{g.date}</p>}
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

export function GreetingDetail() {
  const { slug } = useParams();
  const { t } = useApp();
  const [g, setG] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    api.get(`/public/greetings/${slug}`).then((r) => setG(r.data)).catch(() => setMissing(true));
  }, [slug]);

  if (missing) return <PageHero title={t("not_found")} />;
  if (!g) return null;
  const title = greetingTitle(g);
  const quote = greetingQuote(g);
  const shareText = [...new Set([englishText(g), hindiText(g)].filter(Boolean))].join("\n\n");

  return (
    <div data-testid="greeting-detail">
      <SEO title={title} description={(englishText(g) || hindiText(g) || "").slice(0, 160)} image={g.image} />
      <section className="bg-cream py-16">
        <div className="container-ssf grid items-start gap-10 lg:grid-cols-2">
          {g.image && (
            <div className="card-ssf overflow-hidden rounded-3xl">
              <img src={g.image} alt={title} className="w-full object-cover" data-testid="greeting-image" />
            </div>
          )}
          <div>
            <span className="rounded-full bg-saffron-100 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-saffron-600">{g.occasion || g.category}</span>
            <h1 className="mt-4 font-display text-3xl font-bold tracking-tight text-navy sm:text-4xl" data-testid="greeting-title">{title}</h1>
            <BilingualMessage g={g} testid="greeting-message" />
            {quote && (
              <blockquote className="mt-6 rounded-xl border-l-4 border-saffron-500 bg-white p-5 text-sm italic text-slate-600">
                “{quote}”
                {g.quote_author && <footer className="mt-2 not-italic font-semibold text-navy">— {g.quote_author}</footer>}
              </blockquote>
            )}
            {g.date && <p className="mt-5 flex items-center gap-1.5 text-sm text-slate-500"><CalendarDays size={15} />{g.date}</p>}
            <SenderCard g={g} />
            <div className="mt-6"><ShareButtons title={title} text={shareText} /></div>
          </div>
        </div>
      </section>
    </div>
  );
}
